import {
  Anchor,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';

import { AppShell } from '../../components/app-shell/AppShell';
import { EmptyState, MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { useAuth } from '../../lib/auth-context';

/**
 * /profile — own artist profile (public preview tab).
 *
 * Shows what other users see. The "Edit" button leads to /profile/edit.
 * Built directly against /api/profiles/me/artist; the 404 path renders an
 * onboarding hint instead of an error.
 */

type ArtistProfileResponse = {
  ok: true;
  profile: {
    id: string;
    slug: string;
    bio: string | null;
    city: string | null;
    country: string | null;
    discipline_summary: string | null;
    profile_photo_url: string | null;
    gallery_image_urls: string[];
    links: { kind: string; url: string; label?: string }[];
    party_size_default: number | null;
    dietary_notes: string | null;
    sleeping_preferences: string | null;
    accessibility_needs: string | null;
    verified_at: number | null;
    total_completed_exchanges: number;
    published: boolean;
  };
  art_forms: string[];
};

type ApiError = { ok: false; error: { code: string; message: string } };

async function fetchArtistProfile(): Promise<ArtistProfileResponse | ApiError> {
  const r = await fetch('/api/profiles/me/artist', { credentials: 'include' });
  return (await r.json()) as ArtistProfileResponse | ApiError;
}

function ProfileView() {
  const q = useQuery({ queryKey: ['profile', 'me', 'artist'], queryFn: fetchArtistProfile });
  const { capabilities, user } = useAuth();

  if (q.isPending) {
    return (
      <AppShell tone="content">
        <Loader />
      </AppShell>
    );
  }
  if (q.isError || !q.data || !q.data.ok) {
    // This page is the ARTIST profile. A host with no artist profile lands here
    // by looking for their venue's public page, and the old copy sent them into
    // artist onboarding — a flow that can't complete without an artist profile,
    // so they'd hit a wall at the photo step. Send hosts to their venues instead,
    // and offer the artist role as a deliberate choice rather than a dead end.
    const isHostOnly = capabilities?.isHost === true && capabilities.isArtist !== true;
    if (isHostOnly) {
      return (
        <AppShell tone="content">
          <EmptyState
            title="This is your artist profile — you don't have one yet."
            body="Hosting doesn't need one. Your venue's public page lives under Venues. If you also perform, you can add an artist profile any time."
            action={
              <Group justify="center" gap="sm">
                <Button
                  component={Link}
                  to="/host/venues"
                  variant="gradient"
                  gradient={{ from: 'tide.7', to: 'tide.6', deg: 35 }}
                >
                  Go to my venues
                </Button>
                <Button component={Link} to="/settings/account" variant="subtle">
                  Add an artist profile
                </Button>
              </Group>
            }
          />
        </AppShell>
      );
    }
    return (
      <AppShell tone="content">
        <EmptyState
          title="Your profile isn't set up yet."
          body="Finish onboarding to publish a profile. Hosts read this before they reply."
          action={
            <Button
              component={Link}
              to="/onboarding/artist"
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              Continue onboarding
            </Button>
          }
        />
      </AppShell>
    );
  }

  const { profile, art_forms } = q.data;
  // The heading is what hosts see: the artist's name, not the URL slug.
  const name = user?.displayName ?? profile.slug;

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Public preview"
            title={name}
            lede={profile.discipline_summary ?? undefined}
            action={
              <Button component={Link} to="/profile/edit" variant="light" radius="md">
                Edit profile
              </Button>
            }
          />
          <Group gap="xs" mt="xs">
            {profile.verified_at !== null && (
              <Badge color="brand" variant="light">
                Verified
              </Badge>
            )}
            {profile.published ? (
              <Badge color="teal" variant="light">
                Published
              </Badge>
            ) : (
              <Badge color="yellow" variant="light">
                Unpublished
              </Badge>
            )}
            <Text size="sm" c="ink.6">
              {profile.total_completed_exchanges} completed exchange
              {profile.total_completed_exchanges === 1 ? '' : 's'}
            </Text>
          </Group>
        </MotionSection>

        {profile.verified_at === null && (
          <Card
            withBorder
            padding="lg"
            radius="md"
            style={{
              borderColor: 'var(--mantine-color-brand-2)',
              backgroundColor: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))',
            }}
          >
            <Stack gap="sm">
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                style={{
                  letterSpacing: '0.04em',
                  color: 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))',
                }}
              >
                Trust signal
              </Text>
              <Title order={3}>Get verified.</Title>
              <Text c="ink.7">
                Hosts are 3x more likely to confirm verified artists. We review within 72 hours.
              </Text>
              <Group>
                <Button
                  component={Link}
                  to="/verification"
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                >
                  Verify your identity
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {profile.profile_photo_url !== null && (
          <Image
            src={profile.profile_photo_url}
            alt={`Photo of ${name}`}
            radius="md"
            h={320}
            fit="cover"
          />
        )}

        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Title order={3}>About</Title>
            {profile.bio !== null && profile.bio.length > 0 ? (
              <Text style={{ whiteSpace: 'pre-wrap' }}>{profile.bio}</Text>
            ) : (
              <Text c="dimmed">
                No bio yet. Hosts read this before anything else. A paragraph about what you bring to a room goes a long way.
              </Text>
            )}
            {(profile.city !== null || profile.country !== null) && (
              <Text size="sm" c="dimmed">
                Based in {[profile.city, profile.country].filter((s) => s !== null).join(', ')}
              </Text>
            )}
          </Stack>
        </Paper>

        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Title order={3}>What I bring to a room</Title>
            {art_forms.length > 0 ? (
              <Group gap="xs">
                {art_forms.map((slug) => (
                  <Badge key={slug} variant="outline">
                    {prettySlug(slug)}
                  </Badge>
                ))}
              </Group>
            ) : (
              <Text c="dimmed">No art forms selected yet. Add yours so hosts know what you do.</Text>
            )}
          </Stack>
        </Paper>

        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Title order={3}>On the road</Title>
            <DefList
              rows={[
                ['Default party size', profile.party_size_default],
                ['Dietary notes', profile.dietary_notes],
                ['Sleeping preferences', profile.sleeping_preferences],
                ['Accessibility', profile.accessibility_needs],
              ]}
            />
          </Stack>
        </Paper>

        {profile.links.length > 0 && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Title order={3}>Links</Title>
              <Stack gap={4}>
                {profile.links.map((l) => (
                  <Anchor key={l.url} href={l.url} target="_blank" rel="noreferrer">
                    {l.label ?? prettySlug(l.kind)}
                  </Anchor>
                ))}
              </Stack>
            </Stack>
          </Paper>
        )}

        <Divider />
        <Text size="sm" c="dimmed">
          This is the preview hosts see. Edits land instantly, no draft mode.
        </Text>
      </Stack>
    </AppShell>
  );
}

function DefList({ rows }: { rows: [string, string | number | null][] }) {
  const filled = rows.filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim().length > 0,
  );
  if (filled.length === 0) {
    return <Text c="dimmed">Nothing filled in yet. Hosts see these notes once your stay is confirmed.</Text>;
  }
  return (
    <Stack gap={4}>
      {filled.map(([label, value]) => (
        <Group key={label} gap="sm" wrap="nowrap">
          <Text fw={500} w={180}>
            {label}
          </Text>
          <Text>{String(value)}</Text>
        </Group>
      ))}
    </Stack>
  );
}

function prettySlug(slug: string): string {
  return slug
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export const Route = createFileRoute('/profile/')({
  component: ProfileView,
});
